import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import RightPanel from '../../../RightPanel'
import FlexRow from '../../../../molecules/FlexRow'
import WealthboxTaskDetailModal from '../shared/WealthboxTaskDetailModal'
import WealthboxTaskRow from '../shared/WealthboxTaskRow'
import presets from './presets'

const METRIC_INVALIDATE_KEYS = [
  [QUERY_KEYS.wealthboxDomainQuery, 'metric-preset'],
  [QUERY_KEYS.wealthboxDomainQuery, 'task-list-preset'],
  [QUERY_KEYS.wealthboxDomainQuery, 'list-preset']
]

function WealthboxTaskPanel ({ open, onClose, title, config, contactId }) {
  const { clientId, userId } = useAppContext()
  const [selectedTask, setSelectedTask] = useState(null)

  const presetKey = config?.preset
  const preset = presets[presetKey]
  const enableEdit = config?.enableEdit ?? false
  const formMode = config?.formMode ?? 'simple'
  const scope = config?.scope || 'client'

  // Lazy-load the full task list only when the panel actually opens — the
  // metric tile uses the lighter total_count fetch, so we don't want to pay
  // the pagination cost until the user clicks through.
  const taskListQuery = useQuery({
    queryKey: [QUERY_KEYS.wealthboxDomainQuery, 'task-list-preset', userId, clientId, presetKey, scope, config?.createdByScope, config?.updatedWithinDays, config?.staleAfterDays, config?.includeCompleted, config?.taskType, config?.teamId, contactId],
    queryFn: async () => {
      return preset.fetchData({
        scope,
        contactId,
        clientId,
        fullList: true
      }, config)
    },
    enabled: open && !!preset && (scope === 'client' ? !!contactId : true)
  })

  const tasks = useMemo(
    () => (Array.isArray(taskListQuery.data) ? taskListQuery.data : []),
    [taskListQuery.data]
  )

  const sorted = useMemo(() => {
    if (!tasks.length) return []
    return [...tasks].sort((a, b) => {
      const da = a.due_date ? dayjs(a.due_date).valueOf() : Infinity
      const db = b.due_date ? dayjs(b.due_date).valueOf() : Infinity
      return da - db
    })
  }, [tasks])

  const handleCloseDetail = useCallback(() => setSelectedTask(null), [])

  const isLoading = taskListQuery.isFetching && !tasks.length

  return (
    <RightPanel
      open={open}
      toggleOpen={onClose}
      title={title || 'Tasks'}
      showCloseButton
    >
      <div style={{ paddingBottom: '1.5rem' }}>
        {isLoading
          ? (
            <FlexRow
              className='justify-center items-center'
              style={{
                minHeight: '120px',
                padding: '0 1.5rem',
                color: 'var(--text-color-subtle)',
                fontSize: 'var(--font-size-sm)'
              }}
            >
              Loading tasks…
            </FlexRow>
          )
          : !sorted.length
            ? (
              <FlexRow
                className='justify-center items-center'
                style={{
                  minHeight: '120px',
                  padding: '0 1.5rem',
                  color: 'var(--text-color-subtle)',
                  fontSize: 'var(--font-size-sm)'
                }}
              >
                No current tasks
              </FlexRow>
            )
            : sorted.map(task => (
              <WealthboxTaskRow
                key={task.id}
                task={task}
                onClick={() => setSelectedTask(task)}
              />
            ))}
      </div>

      <WealthboxTaskDetailModal
        open={!!selectedTask}
        onClose={handleCloseDetail}
        task={selectedTask}
        enableEdit={enableEdit}
        formMode={formMode}
        invalidateQueryKeys={METRIC_INVALIDATE_KEYS}
      />
    </RightPanel>
  )
}

WealthboxTaskPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  config: PropTypes.object,
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default WealthboxTaskPanel
